<template>
  <header class="app-header nav-enter" :class="{ scrolled: isScrolled }">
    <!-- 主导航栏 -->
    <nav class="main-nav">
      <div class="container">
        <div class="nav-content">
          <!-- Logo -->
          <router-link to="/" class="logo">
            <img src="@/assets/pic/logo.png" alt="前瞻国际" class="logo-img" />
            <span class="logo-text">前瞻国际</span>
          </router-link>

          <!-- 桌面端导航菜单 -->
          <div class="nav-menu" :class="{ active: mobileMenuOpen }">
            <!-- 移动端关闭按钮 -->
            <button
              class="mobile-close"
              @click="closeMobileMenu"
              aria-label="关闭菜单"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111827"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="nav-link"
              @click="closeMobileMenu"
            >
              {{ $i18n.t(item.label) }}
            </router-link>
          </div>

          <!-- 右侧区域：语言切换 + 移动端菜单按钮 -->
          <div class="nav-right">
            <LangSwitch />
            <button
              class="mobile-toggle"
              @click="toggleMobileMenu"
              aria-label="菜单"
            >
              <span :class="{ open: mobileMenuOpen }"></span>
            </button>
          </div>
        </div>
      </div>
    </nav>

    <!-- 移动端菜单遮罩 -->
    <transition name="mobile-menu">
      <div
        v-if="mobileMenuOpen"
        class="mobile-overlay"
        @click="closeMobileMenu"
      ></div>
    </transition>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import LangSwitch from "./LangSwitch.vue";

const isScrolled = ref(false);
const mobileMenuOpen = ref(false);

const navItems = [
  { path: "/", label: "nav.home" },
  { path: "/company", label: "nav.company" },
  { path: "/about", label: "nav.about" },
  { path: "/products", label: "nav.products" },
  { path: "/download", label: "nav.download" },
  { path: "/news", label: "nav.news" },
  { path: "/contact", label: "nav.contact" },
];

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
  document.body.style.overflow = mobileMenuOpen.value ? "hidden" : "";
};

const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
  document.body.style.overflow = "";
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.app-header {
  position: fixed;
  top: env(safe-area-inset-top);
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s ease;
}

.app-header.scrolled .main-nav {
  background-color: #ffffff;
  box-shadow: 0 0.25rem 1.25rem rgba(0, 0, 0, 0.15);
}

/* 滚动后文字颜色调整为深色 */
.app-header.scrolled .nav-link {
  color: #1a202c;
}

.app-header.scrolled .nav-link:hover,
.app-header.scrolled .nav-link.router-link-active {
  color: #4779b5;
}

.app-header.scrolled .logo-img {
  filter: brightness(0);
}

.app-header.scrolled .mobile-toggle span,
.app-header.scrolled .mobile-toggle span::before,
.app-header.scrolled .mobile-toggle span::after {
  background-color: #111827;
}

/* 主导航 - 默认透明 */
.main-nav {
  background-color: transparent;
  padding: 1.2rem 0;
  transition: all 0.4s ease;
}

.main-nav .container {
  max-width: 87.5rem;
  margin: 0 auto;
  padding: 0 1.25rem;
}

.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
}

.logo-img {
  height: 3rem;
  width: auto;
  display: block;
}
.logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin-left: 0.5rem;
}

.app-header.scrolled .logo-text {
  color: #1a202c;
}

/* 导航菜单 */
.nav-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nav-link {
  padding: 0.63rem 1.13rem;
  color: #fff;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 500;
  position: relative;
  transition: color 0.3s;
  white-space: nowrap;
}

.nav-link::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0.13rem;
  background-color: #4779b5;
  transition: width 0.3s ease;
}

.nav-link:hover,
.nav-link.router-link-active {
  color: #4779b5;
}

.nav-link:hover::after,
.nav-link.router-link-active::after {
  width: 70%;
}

/* 右侧区域 */
.nav-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* 汉堡菜单按钮 */
.mobile-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 2rem;
  height: 2rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.mobile-toggle span,
.mobile-toggle span::before,
.mobile-toggle span::after {
  display: block;
  width: 1.38rem;
  height: 0.13rem;
  background-color: #4779b5;
  border-radius: 0.06rem;
  transition: all 0.3s ease;
}

.mobile-toggle span {
  position: relative;
}

.mobile-toggle span::before,
.mobile-toggle span::after {
  content: "";
  position: absolute;
  left: 0;
}

.mobile-toggle span::before {
  top: -0.44rem;
}

.mobile-toggle span::after {
  bottom: -0.44rem;
}

.mobile-toggle span.open {
  background-color: transparent;
}

.mobile-toggle span.open::before {
  top: 0;
  transform: rotate(45deg);
}

.mobile-toggle span.open::after {
  bottom: 0;
  transform: rotate(-45deg);
}

/* 移动端遮罩 */
.mobile-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 998;
}

/* 移动端菜单关闭按钮（X） */
.mobile-close {
  display: none;
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 3rem;
  height: 3rem;
  background-color: #f0f2f5;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  padding: 0.4rem;
  z-index: 10;
}

.mobile-close svg {
  display: block;
}

/* 响应式设计 */
@media (max-width: 64rem) {
  .nav-link {
    padding: 0.63rem 0.88rem;
    font-size: 0.88rem;
  }
}

@media (max-width: 48rem) {
  .main-nav {
    padding: 0.75rem 0;
  }

  .logo-img {
    height: 1.875rem;
  }
  .logo-text {
    font-size: 1.25rem;
  }

  .mobile-toggle {
    display: flex;
  }

  .nav-menu {
    position: fixed;
    top: 0;
    right: -17.5rem;
    width: 17.5rem;
    height: 100vh;
    background-color: #ffffff;
    flex-direction: column;
    align-items: flex-start;
    padding: 4.5rem 1.88rem 1.88rem;
    gap: 0;
    transition: right 0.3s ease;
    z-index: 999;
    overflow: visible;
  }

  .nav-menu.active {
    right: 0;
  }

  .mobile-close {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .nav-link {
    padding: 0.88rem 0;
    font-size: 1rem;
    width: 100%;
    border-bottom: 0.06rem solid rgba(30, 41, 59, 0.1);
    color: #1a202c;
  }

  .nav-link:hover,
  .nav-link.router-link-active {
    color: #4779B5;
  }

  .nav-link::after {
    display: none;
  }
}

@media (max-width: 30rem) {
  .logo-img {
    height: 1.625rem;
  }
  .logo-text {
    font-size: 1.13rem;
    letter-spacing: 0.06rem;
    /* color: #1a202c; */
  }
}
</style>
